import { functions } from "@/main";
import { recallAdminRead, rememberAdminRead } from "@/utils/AdminReadCache";
import {
  InfoConfigCheckResult,
  InfoConfigKind,
  InfoConfigVersionDetail,
  InfoConfigVersionList,
} from "../models/InfoConfigAdministration";

/**
 * Beschreibungen in the admin portal, through IRES Cloud (Cloud
 * `docs/apis/beschreibungen-for-administration.md`). Each callable has a `…Development` twin; swap to
 * it **locally only** to drive the twin from a dev server, never commit the swap.
 */
const LIST_CALLABLE = "listInfoConfigVersionsForAdministration";
const GET_CALLABLE = "getInfoConfigVersionForAdministration";
const CHECK_CALLABLE = "checkInfoConfigUploadForAdministration";
const PUBLISH_CALLABLE = "publishInfoConfigVersionForAdministration";
const SET_TARGET_CALLABLE = "setInfoConfigTargetForAdministration";
const RETIRE_CALLABLE = "retireInfoConfigVersionForAdministration";

/** Every version, room and legacy leak detection, with its targets. `MANAGE_CLIENTS`. */
export async function listInfoConfigVersions(): Promise<InfoConfigVersionList> {
  const data = (await functions.httpsCallable(LIST_CALLABLE)()).data as Partial<InfoConfigVersionList> | null;
  const room = data?.room;
  const leakDetection = data?.leakDetection;
  const list = {
    room: Array.isArray(room) ? room : [],
    leakDetection: Array.isArray(leakDetection) ? leakDetection : [],
    globalRoomTarget: data?.globalRoomTarget ?? null,
    globalLeakDetectionTarget: data?.globalLeakDetectionTarget ?? null,
  };
  rememberAdminRead(LIST_CALLABLE, null, list);
  return list;
}

/** The last answer of {@link listInfoConfigVersions} in this tab, or null (`AdminReadCache.ts`). */
export function cachedInfoConfigVersions(): InfoConfigVersionList | null {
  return recallAdminRead(LIST_CALLABLE, null);
}

/** One version's rows, problems and a five-minute download link. Rejects with `not-found`. */
export async function getInfoConfigVersion(kind: InfoConfigKind, version: string): Promise<InfoConfigVersionDetail> {
  const data = (await functions.httpsCallable(GET_CALLABLE)({ kind, version })).data as InfoConfigVersionDetail | null;
  if (!data || !Array.isArray(data.rows)) {
    throw new Error("The version response carried no rows.");
  }
  return data;
}

/**
 * Checks a new room version without storing it: its problems, whether the number is free and above the
 * newest, and its differences from `compareWith` (default: the global target). Named SysAdmin.
 */
export async function checkInfoConfigUpload(
  version: string,
  contentBase64: string,
  compareWith: string | null
): Promise<InfoConfigCheckResult> {
  const data = (await functions.httpsCallable(CHECK_CALLABLE)({ version, contentBase64, compareWith })).data as InfoConfigCheckResult | null;
  if (!data || !Array.isArray(data.problems)) {
    throw new Error("The check response carried no result.");
  }
  return data;
}

/**
 * Publishes a checked room version. IRES Cloud runs the check again and refuses with
 * `failed-precondition` (`CHECK_FAILED`, `WARNINGS_NOT_ACKNOWLEDGED`) or `already-exists`.
 */
export async function publishInfoConfigVersion(version: string, contentBase64: string, acknowledgedWarnings: boolean): Promise<void> {
  await functions.httpsCallable(PUBLISH_CALLABLE)({ version, contentBase64, acknowledgedWarnings });
}

/** Sets the global room target (named SysAdmin). */
export async function setGlobalInfoConfigTarget(version: string): Promise<void> {
  await functions.httpsCallable(SET_TARGET_CALLABLE)({ scope: "GLOBAL", version });
}

/** Sets a client's own room target, or with `null` lets it follow the global one (`MANAGE_CLIENTS`). */
export async function setClientInfoConfigTarget(clientId: string, version: string | null): Promise<void> {
  await functions.httpsCallable(SET_TARGET_CALLABLE)({ scope: "CLIENT", clientId, version });
}

/** Retires a room version; the file stays. Refused (`VERSION_IS_TARGET`) for a target. Named SysAdmin. */
export async function retireInfoConfigVersion(version: string): Promise<void> {
  await functions.httpsCallable(RETIRE_CALLABLE)({ version });
}
